// src/pages/profile/sections/UserProfileBilling.tsx
import React, { useState, useEffect } from "react";
import { Typography, Space, Alert, Row, Col, Statistic } from "antd";
import { CreditCardOutlined, FileTextOutlined, DollarOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { SectionCard, InlineLoading } from "../../../components/shared";
import PaymentMethodList from "../../../components/finance/PaymentMethodList";
import AddPaymentMethodModal from "../../../components/finance/AddPaymentMethodModal";
import { getFinanceCustomer, createFinanceCustomer, fetchUserDashboard } from "../../../api/finance/FinanceApi";
import { checkFinanceIntegrationForCompany } from "../../../api/admin/integrationApi";
import { StripeCustomer, UserDashboardStats } from "../../../types/finance/FinanceTypes";
import { useNotifications } from "../../../context/NotificationContext";
import { formatCurrency } from "../../../api/finance/FinanceApi";
import { User } from "../../../types/admin/UserTypes";

const { Text } = Typography;

const UserProfileBilling: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [stripeEnabled, setStripeEnabled] = useState(false);
	const [customer, setCustomer] = useState<StripeCustomer | null>(null);
	const [stats, setStats] = useState<UserDashboardStats | null>(null);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const { showError } = useNotifications();

	const user: User = getSessionUser();

	useEffect(() => {
		initialize();
	}, []);

	const initialize = async () => {
		setLoading(true);
		try {
			// Check if Stripe integration is enabled
			const { isActive } = await checkFinanceIntegrationForCompany();

			if (!isActive) {
				setStripeEnabled(false);
				setLoading(false);
				return;
			}

			setStripeEnabled(true);

			// Try to get existing Stripe customer
			try {
				// Backend should return customer based on user ID, as we add more integrations we will set the customer based on the type of integration that the clinic uses
				const existingCustomer = await getFinanceCustomer(user.id);
				if (existingCustomer.stripe_customer) {
					setCustomer(existingCustomer.stripe_customer);
				}
			} catch (error: any) {
				// Customer doesn't exist, create one automatically
				if (error.response?.status === 400) {
					try {
						const newCustomer = await createFinanceCustomer({
							userId: user.id.toString(),
							email: user.email,
							name: `${user.first_name} ${user.last_name}`,
							phone: user.phone,
						});

						if (newCustomer.stripe_customer) {
							setCustomer(newCustomer.stripe_customer);
						}
					} catch (createError) {
						console.error("Error creating Stripe customer:", createError);
						showError("Failed to create billing account");
					}
				} else {
					throw error;
				}
			}

			// Load user dashboard stats
			try {
				const dashboardStats = await fetchUserDashboard(user.id.toString());
				setStats(dashboardStats);
			} catch (error) {
				console.error("Error loading dashboard stats:", error);
			}
		} catch (error) {
			console.error("Error initializing billing:", error);
			showError("Failed to load billing information");
		} finally {
			setLoading(false);
		}
	};

	const handleAddSuccess = () => {
		setRefreshTrigger((prev) => prev + 1);
	};

	if (loading) {
		return <InlineLoading minHeight={300} message="Loading billing information…" />;
	}

	if (!stripeEnabled) {
		return (
			<Alert
				message="Billing Not Available"
				description="Your organization has not enabled billing functionality. Please contact your administrator for more information."
				type="info"
				showIcon
				icon={<CreditCardOutlined />}
			/>
		);
	}

	if (!customer) {
		return <InlineLoading minHeight={300} message="Setting up your billing account…" />;
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Billing Overview */}
			{stats && (
				<SectionCard title="Billing Overview">
					<Row gutter={[16, 16]}>
						<Col xs={24} md={8}>
							<Statistic
								title="Total Paid"
								value={stats.payments.totalAmount}
								precision={2}
								prefix={<DollarOutlined style={{ color: "var(--mg-success)" }} />}
								valueStyle={{ color: "var(--mg-success)" }}
								formatter={(value) => formatCurrency(Number(value))}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Statistic
								title="Pending Payments"
								value={stats.payments.pending}
								prefix={<CreditCardOutlined style={{ color: "var(--mg-warning)" }} />}
								valueStyle={{ color: "var(--mg-warning)" }}
								suffix={
									<Text type="secondary" style={{ fontSize: 14 }}>
										payments
									</Text>
								}
							/>
						</Col>
						<Col xs={24} md={8}>
							<Statistic
								title="Outstanding Invoices"
								value={stats.invoices.pending + stats.invoices.overdue}
								prefix={<FileTextOutlined style={{ color: stats.invoices.overdue > 0 ? "var(--mg-danger)" : "var(--mg-primary)" }} />}
								valueStyle={{ color: stats.invoices.overdue > 0 ? "var(--mg-danger)" : "var(--mg-primary)" }}
								suffix={
									<Text type="secondary" style={{ fontSize: 14 }}>
										invoices
									</Text>
								}
							/>
						</Col>
					</Row>
				</SectionCard>
			)}

			{/* Customer Information */}
			<SectionCard title="Billing Information">
				<Space direction="vertical" size="middle">
					<div>
						<Text type="secondary" style={{ fontSize: 11 }}>Customer ID</Text>
						<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{customer.id}</Text>
					</div>
					<div>
						<Text type="secondary" style={{ fontSize: 11 }}>Email</Text>
						<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{customer.email}</Text>
					</div>
					{customer.name && (
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>Name</Text>
							<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{customer.name}</Text>
						</div>
					)}
					{customer.phone && (
						<div>
							<Text type="secondary" style={{ fontSize: 11 }}>Phone</Text>
							<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>{customer.phone}</Text>
						</div>
					)}
				</Space>
			</SectionCard>

			{/* Payment Methods */}
			<SectionCard title="Payment Methods" noPadding>
				<div style={{ padding: 20 }}>
					<PaymentMethodList customerId={customer.id} onAddNew={() => setAddModalVisible(true)} refreshTrigger={refreshTrigger} />
				</div>
			</SectionCard>

			{/* Add Payment Method Modal */}
			<AddPaymentMethodModal visible={addModalVisible} onClose={() => setAddModalVisible(false)} onSuccess={handleAddSuccess} customerId={customer.id} />
		</div>
	);
};

export default UserProfileBilling;
